import { z } from "zod";

/**
 * The public catalog (`GET /api/public/store`): the phone's StoreListing
 * without the bundle. Same-origin through the Pages function, so asset URLs
 * resolve to this site.
 */
const permissionSchema = z.object({
  type: z.string(),
  required: z.boolean().optional(),
  description: z.string().optional(),
});

const hardwareSchema = z.object({
  type: z.string(),
  level: z.string(),
  description: z.string().optional(),
});

const screenshotSchema = z.object({
  url: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});

export const listingSchema = z.object({
  packageName: z.string(),
  name: z.string(),
  tagline: z.string(),
  description: z.string(),
  tags: z.array(z.string()),
  featured: z.boolean(),
  author: z.object({ name: z.string(), verified: z.boolean() }),
  version: z.string(),
  type: z.enum(["standard", "background", "dashboard"]),
  alwaysOn: z.boolean(),
  minHostVersion: z.string().nullable(),
  permissions: z.array(permissionSchema),
  hardwareRequirements: z.array(hardwareSchema),
  iconUrl: z.string().nullable(),
  screenshots: z.array(screenshotSchema),
  bundleSizeBytes: z.number(),
  publishedAt: z.string(),
  updatedAt: z.string(),
});

const catalogSchema = z.object({
  generatedAt: z.string(),
  listings: z.array(listingSchema),
});

export type StoreListing = z.infer<typeof listingSchema>;
export type StoreCatalog = z.infer<typeof catalogSchema>;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  const response = await fetch(`/api/public/store${path}`, { headers: { accept: "application/json" } });
  if (!response.ok) {
    let message = `Request failed with ${response.status}`;
    try {
      const body = (await response.json()) as { error_description?: string; error?: string };
      message = body.error_description || body.error || message;
    } catch {
      // Not JSON; keep the status message.
    }
    throw new ApiError(message, response.status);
  }
  return schema.parse(await response.json());
}

export function fetchCatalog(): Promise<StoreCatalog> {
  return request("", catalogSchema);
}

export function fetchListing(packageName: string): Promise<StoreListing> {
  return request(`/${encodeURIComponent(packageName)}`, listingSchema);
}

/** Same filters the phone store applies, so both surfaces agree on what a query finds. */
export function searchListings(query: string, listings: StoreListing[]): StoreListing[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return listings;
  return listings.filter(listing =>
    [listing.name, listing.tagline, listing.author.name, listing.packageName, ...listing.tags].some(field =>
      field.toLowerCase().includes(needle),
    ),
  );
}

export function filterByTag(tag: string | "all", listings: StoreListing[]): StoreListing[] {
  return tag === "all" ? listings : listings.filter(listing => listing.tags.includes(tag));
}

/** Deterministic tile colour for listings without an icon; mirrors the phone's palette. */
export function accentFor(packageName: string): string {
  const palette = ["#2563EB", "#7C3AED", "#0891B2", "#059669", "#DB2777", "#D97706", "#DC2626", "#4F46E5"];
  let hash = 0;
  for (let i = 0; i < packageName.length; i++) hash = (hash * 31 + packageName.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length]!;
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "—";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
